<script setup>
	const processSteps = [
		{
			number: "01",
			title: "Diagnose the chaos",
			description: "We read through the code the AI actually wrote, map where it's fragile, and trace what's quietly holding the whole thing together. Then we explain it back to you in plain English — no jargon, no lectures.",
			takeaway: "A clear, honest picture of what you really have",
		},
		{
			number: "02",
			title: "Stabilize the build",
			description: "We stop the bleeding first: the crashes, the flaky deploys, the changes that break three other things. Clean, tested, maintainable code replaces the duct tape, written by people who have shipped real systems for two decades.",
			takeaway: "Software that stops breaking every time you touch it",
		},
		{
			number: "03",
			title: "Harden and document",
			description: "Once it's stable, we make it durable — sensible architecture, real tests on the paths that matter, and documentation so the next person (or the next tool) isn't starting from zero. No more one-person mystery code.",
			takeaway: "A codebase your team can actually understand and grow",
		},
		{
			number: "04",
			title: "Hand it back, working",
			description: "You get something you own outright: clear, scalable, and yours. We hand off cleanly, stay available for questions, and make sure you're never locked into us to keep your own software running.",
			takeaway: "Confidence — and no vendor lock-in",
		},
	];

	const principles = [
		{
			title: "Senior engineers, not juniors",
			description: "The people who diagnose and fix your software are experienced architects who have done this for years — not a rotating cast of trainees learning on your dime.",
			icon: "badge",
		},
		{
			title: "Tested and documented",
			description: "We don't just make it work today. We leave behind real tests and clear documentation so it keeps working long after we're gone.",
			icon: "check",
		},
		{
			title: "Plain-English communication",
			description: "You'll always know what we're doing and why. We translate the technical reality into language you can actually make decisions with.",
			icon: "chat",
		},
		{
			title: "No lock-in — it's yours",
			description: "Your software belongs to you. We build it so you, or anyone you choose later, can pick it up and keep going without us.",
			icon: "unlock",
		},
	];
</script>

<template>
	<div class="how">
		<section class="hero">
			<div class="hero-bg" aria-hidden="true">
				<span class="hero-blob hero-blob-one"></span>
				<span class="hero-blob hero-blob-two"></span>
				<span class="hero-grid"></span>
			</div>
			<div class="hero-inner">
				<span class="eyebrow rise">How we work</span>
				<h1 class="hero-title rise rise-1">From a tangled mess to software you can trust.</h1>
				<p class="hero-lead rise rise-2">
					When an AI ships you something that almost works, the hard part isn't writing more
					code — it's understanding what's there and making it solid. Here's the calm, proven
					path we take to get you from anxiety to confidence.
				</p>
				<div class="hero-actions rise rise-3">
					<RouterLink :to="{ name: 'contact' }" class="btn btn-primary">Get Started</RouterLink>
					<RouterLink :to="{ name: 'services' }" class="btn btn-ghost">Services &amp; pricing</RouterLink>
				</div>
				<ul class="hero-assurances rise rise-4">
					<li><span class="assurance-dot" aria-hidden="true"></span> 20+ years shipping real software</li>
					<li><span class="assurance-dot" aria-hidden="true"></span> Tests &amp; documentation included</li>
					<li><span class="assurance-dot" aria-hidden="true"></span> A straight answer, always</li>
				</ul>
			</div>
		</section>

		<section class="process">
			<div class="process-inner">
				<header class="block-header">
					<span class="block-eyebrow">The process</span>
					<h2 class="block-heading">Four steps out of the mess</h2>
					<p class="block-sub">No mystery, no guessing game — you always know exactly where things stand.</p>
				</header>

				<ol class="process-list">
					<li v-for="processStep in processSteps" :key="processStep.number" class="process-step">
						<span class="process-node">{{ processStep.number }}</span>
						<div class="process-content">
							<h3 class="process-title">{{ processStep.title }}</h3>
							<p class="process-description">{{ processStep.description }}</p>
							<p class="process-takeaway">
								<span class="process-takeaway-icon" aria-hidden="true">
									<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><path d="M5 13l4 4L19 7" /></svg>
								</span>
								{{ processStep.takeaway }}
							</p>
						</div>
					</li>
				</ol>
			</div>
		</section>

		<section class="principles">
			<div class="principles-inner">
				<header class="block-header">
					<span class="block-eyebrow">Why it works</span>
					<h2 class="block-heading">What you can count on</h2>
				</header>

				<div class="principles-grid">
					<article v-for="principle in principles" :key="principle.title" class="principle-card">
						<span class="principle-icon" aria-hidden="true">
							<svg v-if="principle.icon === 'badge'" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 15a4 4 0 100-8 4 4 0 000 8z" /><path d="M9 11.5L7 21l5-3 5 3-2-9.5" /></svg>
							<svg v-else-if="principle.icon === 'check'" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M9 12l2 2 4-4" /><circle cx="12" cy="12" r="9" /></svg>
							<svg v-else-if="principle.icon === 'chat'" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 11.5a8.38 8.38 0 01-8.5 8.5 8.5 8.5 0 01-3.8-.9L3 21l1.9-5.7a8.5 8.5 0 01-.9-3.8 8.38 8.38 0 018.5-8.5 8.5 8.5 0 018.5 8.5z" /></svg>
							<svg v-else viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="4" y="11" width="16" height="9" rx="2" /><path d="M8 11V8a4 4 0 018 0" /></svg>
						</span>
						<h3 class="principle-title">{{ principle.title }}</h3>
						<p class="principle-description">{{ principle.description }}</p>
					</article>
				</div>
			</div>
		</section>

		<section class="promise">
			<div class="promise-inner">
				<span class="promise-mark" aria-hidden="true">&ldquo;</span>
				<p class="promise-text">
					The first conversation is free — and honest. We'll tell you straight whether software
					is even the answer, and what it would really take to fix it. If it's not worth saving,
					we'll tell you that too.
				</p>
				<span class="promise-by">— Chad Greer, Lucentary</span>
			</div>
		</section>

		<section class="cta">
			<div class="cta-inner">
				<div class="cta-card">
					<h2 class="cta-title">Ready to talk through your situation?</h2>
					<p class="cta-lead">
						Tell us what's broken. No script, no pressure, no fluff — just experienced people
						who can tell you exactly what's going on and how to fix it.
					</p>
					<div class="cta-actions">
						<RouterLink :to="{ name: 'contact' }" class="btn btn-on-blue">Get Started</RouterLink>
						<RouterLink :to="{ name: 'faq' }" class="btn btn-ghost-light">Read the FAQ</RouterLink>
					</div>
					<p class="cta-offer">New clients get <strong>50% off the first 6 weeks.</strong></p>
				</div>
			</div>
		</section>
	</div>
</template>

<style scoped>
	.how {
		--bg: #f5f9fd;
		--surface: #ffffff;
		--surface-muted: #eef4fb;
		--border: #e2ecf6;
		--border-strong: #cddcec;
		--navy: #0f2742;
		--ink: #16314f;
		--ink-soft: #4a5d75;
		--ink-muted: #7589a1;
		--sky: #0ea5e9;
		--sky-deep: #0284c7;
		--blue: #2563eb;
		--blue-deep: #1d4ed8;
		--blue-soft: #dceafe;

		font-family: "Poppins", "Segoe UI", -apple-system, BlinkMacSystemFont, "Helvetica Neue", Arial, sans-serif;
		color: var(--ink);
		background: var(--bg);
		overflow-x: hidden;
	}

	.hero {
		position: relative;
		isolation: isolate;
		padding: clamp(3.5rem, 8vw, 7rem) 1.5rem clamp(3rem, 6vw, 5.5rem);
		overflow: hidden;
	}

	.hero-bg {
		position: absolute;
		inset: 0;
		z-index: -1;
		pointer-events: none;
	}

	.hero-blob {
		position: absolute;
		border-radius: 50%;
		filter: blur(20px);
	}

	.hero-blob-one {
		top: -180px;
		right: -120px;
		width: 520px;
		height: 520px;
		background: radial-gradient(circle at 30% 30%, rgba(37, 99, 235, 0.16), transparent 70%);
	}

	.hero-blob-two {
		bottom: -200px;
		left: -140px;
		width: 460px;
		height: 460px;
		background: radial-gradient(circle at 60% 40%, rgba(14, 165, 233, 0.14), transparent 70%);
	}

	.hero-grid {
		position: absolute;
		inset: 0;
		background-image:
			linear-gradient(rgba(15, 39, 66, 0.04) 1px, transparent 1px),
			linear-gradient(90deg, rgba(15, 39, 66, 0.04) 1px, transparent 1px);
		background-size: 44px 44px;
		-webkit-mask-image: radial-gradient(ellipse 70% 60% at 50% 30%, #000 30%, transparent 75%);
		mask-image: radial-gradient(ellipse 70% 60% at 50% 30%, #000 30%, transparent 75%);
	}

	.hero-inner {
		max-width: 860px;
		margin: 0 auto;
		text-align: center;
	}

	.eyebrow {
		display: inline-block;
		padding: 0.45rem 1rem;
		border-radius: 999px;
		background: var(--blue-soft);
		color: var(--blue-deep);
		font-size: 0.82rem;
		font-weight: 700;
		letter-spacing: 0.12em;
		text-transform: uppercase;
		margin-bottom: 1.5rem;
	}

	.hero-title {
		font-family: "Fraunces", Georgia, "Times New Roman", serif;
		font-size: clamp(2.5rem, 5.5vw, 4.25rem);
		line-height: 1.04;
		font-weight: 600;
		letter-spacing: -0.02em;
		color: var(--navy);
		margin: 0 0 1.5rem;
	}

	.hero-lead {
		font-size: clamp(1.1rem, 1.5vw, 1.32rem);
		line-height: 1.7;
		color: var(--ink-soft);
		max-width: 62ch;
		margin: 0 auto 2.25rem;
	}

	.hero-actions {
		display: flex;
		gap: 1rem;
		justify-content: center;
		flex-wrap: wrap;
		margin-bottom: 2.5rem;
	}

	.btn {
		display: inline-flex;
		align-items: center;
		justify-content: center;
		padding: 0.95rem 1.95rem;
		border-radius: 12px;
		font-family: inherit;
		font-size: 1.05rem;
		font-weight: 600;
		text-decoration: none;
		cursor: pointer;
		border: 1px solid transparent;
		transition: transform 0.2s ease, box-shadow 0.2s ease, background 0.2s ease, border-color 0.2s ease, color 0.2s ease;
	}

	.btn-primary {
		background: var(--blue);
		color: #ffffff;
		box-shadow: 0 14px 28px -14px rgba(37, 99, 235, 0.7);
	}

	.btn-primary:hover {
		transform: translateY(-2px);
		background: var(--blue-deep);
		box-shadow: 0 20px 34px -16px rgba(37, 99, 235, 0.75);
	}

	.btn-primary:focus-visible {
		outline: 3px solid rgba(37, 99, 235, 0.4);
		outline-offset: 2px;
	}

	.btn-ghost {
		background: var(--surface);
		color: var(--ink);
		border-color: var(--border-strong);
	}

	.btn-ghost:hover {
		transform: translateY(-2px);
		border-color: var(--blue);
		color: var(--blue-deep);
	}

	.btn-ghost:focus-visible {
		outline: 3px solid rgba(15, 39, 66, 0.25);
		outline-offset: 2px;
	}

	.btn-on-blue {
		background: #ffffff;
		color: var(--blue-deep);
		box-shadow: 0 14px 30px -14px rgba(0, 0, 0, 0.5);
	}

	.btn-on-blue:hover {
		color: var(--blue-deep);
		transform: translateY(-2px);
		background: #f1f6ff;
		box-shadow: 0 20px 36px -16px rgba(0, 0, 0, 0.55);
	}

	.btn-ghost-light {
		background: transparent;
		color: #ffffff;
		border-color: rgba(255, 255, 255, 0.45);
	}

	.btn-ghost-light:hover {
		transform: translateY(-2px);
		background: rgba(255, 255, 255, 0.12);
		border-color: #ffffff;
	}

	.btn-ghost-light:focus-visible {
		outline: 3px solid rgba(255, 255, 255, 0.6);
		outline-offset: 2px;
	}

	.hero-assurances {
		display: flex;
		flex-wrap: wrap;
		justify-content: center;
		gap: 0.6rem 1.75rem;
		list-style: none;
		margin: 0;
		padding: 0;
		font-size: 0.96rem;
		font-weight: 500;
		color: var(--ink-soft);
	}

	.hero-assurances li {
		display: inline-flex;
		align-items: center;
		gap: 0.55rem;
	}

	.assurance-dot {
		width: 7px;
		height: 7px;
		border-radius: 50%;
		background: var(--sky);
		flex-shrink: 0;
	}

	.process {
		padding: clamp(3.5rem, 7vw, 6rem) 1.5rem;
		background: var(--surface);
		border-top: 1px solid var(--border);
		border-bottom: 1px solid var(--border);
	}

	.process-inner {
		max-width: 820px;
		margin: 0 auto;
	}

	.block-header {
		text-align: center;
		margin-bottom: 3rem;
	}

	.block-eyebrow {
		display: inline-block;
		font-size: 0.85rem;
		font-weight: 700;
		letter-spacing: 0.12em;
		text-transform: uppercase;
		color: var(--blue-deep);
		margin-bottom: 0.7rem;
	}

	.block-heading {
		font-family: "Fraunces", Georgia, "Times New Roman", serif;
		font-size: clamp(1.9rem, 3.2vw, 2.9rem);
		font-weight: 600;
		letter-spacing: -0.02em;
		color: var(--navy);
		margin: 0;
	}

	.block-sub {
		font-size: 1.05rem;
		line-height: 1.6;
		color: var(--ink-soft);
		max-width: 48ch;
		margin: 0.9rem auto 0;
	}

	.process-list {
		position: relative;
		display: grid;
		gap: 2.25rem;
		margin: 0;
		padding: 0;
		list-style: none;
	}

	.process-list::before {
		content: "";
		position: absolute;
		left: 27px;
		top: 12px;
		bottom: 12px;
		width: 2px;
		background: linear-gradient(var(--blue), var(--border));
	}

	.process-step {
		position: relative;
		display: grid;
		grid-template-columns: 56px 1fr;
		gap: 1.5rem;
		align-items: start;
	}

	.process-node {
		position: relative;
		z-index: 1;
		display: inline-flex;
		align-items: center;
		justify-content: center;
		width: 56px;
		height: 56px;
		border-radius: 50%;
		background: var(--blue);
		color: #ffffff;
		font-family: "Fraunces", Georgia, serif;
		font-size: 1.45rem;
		font-weight: 600;
		box-shadow: 0 14px 28px -14px rgba(37, 99, 235, 0.8);
	}

	.process-content {
		padding-top: 0.35rem;
	}

	.process-title {
		font-family: "Fraunces", Georgia, "Times New Roman", serif;
		font-size: clamp(1.35rem, 2.2vw, 1.7rem);
		font-weight: 600;
		color: var(--navy);
		margin: 0 0 0.6rem;
	}

	.process-description {
		font-size: 1.05rem;
		line-height: 1.7;
		color: var(--ink-soft);
		margin: 0 0 1rem;
	}

	.process-takeaway {
		display: inline-flex;
		align-items: center;
		gap: 0.6rem;
		padding: 0.5rem 0.95rem 0.5rem 0.6rem;
		border-radius: 999px;
		background: var(--blue-soft);
		color: var(--blue-deep);
		font-size: 0.95rem;
		font-weight: 600;
		margin: 0;
	}

	.process-takeaway-icon {
		display: inline-flex;
		align-items: center;
		justify-content: center;
		width: 22px;
		height: 22px;
		border-radius: 50%;
		background: var(--blue);
		color: #ffffff;
		flex-shrink: 0;
	}

	.process-takeaway-icon svg {
		width: 12px;
		height: 12px;
	}

	.principles {
		padding: clamp(3.5rem, 7vw, 6rem) 1.5rem;
		background: var(--bg);
	}

	.principles-inner {
		max-width: 1080px;
		margin: 0 auto;
	}

	.principles-grid {
		display: grid;
		grid-template-columns: repeat(2, 1fr);
		gap: 1.5rem;
	}

	.principle-card {
		padding: 2rem 1.85rem;
		border-radius: 18px;
		background: var(--surface);
		border: 1px solid var(--border);
		transition: transform 0.25s ease, box-shadow 0.25s ease, border-color 0.25s ease;
	}

	.principle-card:hover {
		transform: translateY(-4px);
		border-color: var(--blue);
		box-shadow: 0 24px 44px -30px rgba(15, 39, 66, 0.5);
	}

	.principle-icon {
		display: inline-flex;
		align-items: center;
		justify-content: center;
		width: 52px;
		height: 52px;
		border-radius: 14px;
		background: var(--blue-soft);
		color: var(--blue-deep);
		margin-bottom: 1.25rem;
	}

	.principle-icon svg {
		width: 26px;
		height: 26px;
	}

	.principle-title {
		font-size: 1.22rem;
		font-weight: 700;
		color: var(--navy);
		margin: 0 0 0.6rem;
	}

	.principle-description {
		font-size: 1.02rem;
		line-height: 1.65;
		color: var(--ink-soft);
		margin: 0;
	}

	.promise {
		padding: clamp(3rem, 6vw, 5rem) 1.5rem;
		background: var(--surface);
		border-top: 1px solid var(--border);
	}

	.promise-inner {
		max-width: 760px;
		margin: 0 auto;
		text-align: center;
	}

	.promise-mark {
		display: block;
		font-family: "Fraunces", Georgia, serif;
		font-size: 4.5rem;
		line-height: 0.4;
		color: var(--blue);
		margin-bottom: 0.5rem;
	}

	.promise-text {
		font-family: "Fraunces", Georgia, "Times New Roman", serif;
		font-size: clamp(1.4rem, 2.4vw, 1.95rem);
		line-height: 1.45;
		font-weight: 500;
		color: var(--navy);
		margin: 0 0 1.25rem;
	}

	.promise-by {
		font-size: 0.98rem;
		font-weight: 600;
		color: var(--ink-muted);
	}

	.cta {
		padding: clamp(3.5rem, 6vw, 5.5rem) 1.5rem clamp(4rem, 7vw, 6.5rem);
		background: var(--bg);
	}

	.cta-inner {
		max-width: 1000px;
		margin: 0 auto;
	}

	.cta-card {
		padding: clamp(3rem, 5vw, 4.5rem) clamp(1.75rem, 4vw, 4rem);
		border-radius: 24px;
		background: linear-gradient(135deg, var(--blue) 0%, var(--sky-deep) 100%);
		color: #ffffff;
		text-align: center;
		box-shadow: 0 34px 64px -36px rgba(37, 99, 235, 0.7);
	}

	.cta-title {
		font-family: "Fraunces", Georgia, "Times New Roman", serif;
		font-size: clamp(1.9rem, 3.5vw, 3rem);
		font-weight: 600;
		line-height: 1.1;
		letter-spacing: -0.02em;
		margin: 0 auto 1.25rem;
		max-width: 22ch;
		color: #ffffff;
	}

	.cta-lead {
		font-size: clamp(1.08rem, 1.4vw, 1.25rem);
		line-height: 1.6;
		color: rgba(255, 255, 255, 0.92);
		max-width: 54ch;
		margin: 0 auto 2.25rem;
	}

	.cta-actions {
		display: flex;
		gap: 1rem;
		justify-content: center;
		flex-wrap: wrap;
		margin-bottom: 1.5rem;
	}

	.cta-offer {
		font-size: 1.05rem;
		color: rgba(255, 255, 255, 0.92);
		margin: 0;
	}

	.cta-offer strong { color: #ffffff; }

	.rise {
		opacity: 0;
		transform: translateY(16px);
		animation: rise-in 0.6s cubic-bezier(0.22, 1, 0.36, 1) forwards;
	}

	.rise-1 { animation-delay: 0.08s; }
	.rise-2 { animation-delay: 0.16s; }
	.rise-3 { animation-delay: 0.24s; }
	.rise-4 { animation-delay: 0.32s; }

	@keyframes rise-in {
		to {
			opacity: 1;
			transform: none;
		}
	}

	@media (max-width: 760px) {
		.principles-grid {
			grid-template-columns: 1fr;
		}
	}

	@media (max-width: 640px) {
		.hero-actions,
		.cta-actions {
			flex-direction: column;
			align-items: stretch;
		}

		.btn {
			width: 100%;
		}

		.process-step {
			grid-template-columns: 44px 1fr;
			gap: 1.1rem;
		}

		.process-node {
			width: 44px;
			height: 44px;
			font-size: 1.2rem;
		}

		.process-list::before {
			left: 21px;
		}
	}

	@media (prefers-reduced-motion: reduce) {
		.rise {
			opacity: 1;
			transform: none;
			animation: none;
		}
	}
</style>